import { useMemo, useState } from "react";
import { AppLabels } from "./localization";
import { EmptyFormSubmission, FormSubmission, HomeState, KitCodesJson, ShipmentType, Location } from "./types";

export type AvailableLocation = {
    label: string;
    value: string;
    options: Location[];
}

export type HomeMethods = Omit<HomeState, 'lang' | 'isPostMessage' | 'postMessageReferrer' | 'locationOptions'> & {
    setData: React.Dispatch<React.SetStateAction<FormSubmission>>
    setUserConfig: React.Dispatch<React.SetStateAction<HomeState['userConfig']>>
    setIsSaving: (isSaving: boolean) => void
    setHasSubmitted: (hasSubmitted: boolean) => void
    setValidated: (validated: boolean) => void
    valid: boolean
    setValid: (valid: boolean) => void
    setServerError: (serverError: string) => void
    setGeneratedTokensJson: (generatedTokensJson: KitCodesJson) => void
    setConfirmAddress: (confirmAddress: boolean) => void
    setReady: (ready: boolean) => void
    setPickupProvince: (pickupProvince: string) => void
    setAmountNeededOptions: React.Dispatch<React.SetStateAction<HomeState['amountNeededOptions']>>
    goFreddyUrl: string,
    availableLocations: AvailableLocation[]
}

const useHomeMethods = (lang: string, userId: string | undefined): HomeMethods => {
    const goFreddyUrl = useMemo(() => {
        let goFreddyUrl = process.env.REACT_APP_GO_FREDDY_URL || '';
        if(goFreddyUrl !== '') {
            goFreddyUrl = goFreddyUrl.replace('{userId}', userId || '');
        }
        return goFreddyUrl;
    }, [ userId ]);

    const [ data, setData ] = useState<FormSubmission>({...EmptyFormSubmission});
    const [ userConfig, setUserConfig ] = useState<HomeState['userConfig']>(undefined);
    const [ isSaving, setIsSaving ] = useState<boolean>(false);
    const [ hasSubmitted, setHasSubmitted ] = useState<boolean>(false);
    const [ validated, setValidated ] = useState<boolean>(false);
    const [ valid, setValid ] = useState<boolean>(false);
    const [ serverError, setServerError ] = useState<string>('');
    const [ generatedTokensJson, setGeneratedTokensJson ] = useState<KitCodesJson>({});
    const [ confirmAddress, setConfirmAddress ] = useState<boolean>(false);
    const [ ready, setReady ] = useState<boolean>(false);
    const [ pickupProvince, setPickupProvince ] = useState<string>('');
    const [ amountNeededOptions, setAmountNeededOptions ] = useState<HomeState['amountNeededOptions']>([]);

    const shippingTypeOptions = [
      { value: ShipmentType.Delivery, label: AppLabels.ShipmentTypeDelivery },
      { value: ShipmentType.Pickup, label: AppLabels.ShipmentTypePickup },
    ];

    const provinces = useMemo(() => ([
      { value: 'AB', label: AppLabels.Alberta },
      { value: 'BC', label: AppLabels.BritishColumbia },
      { value: 'MB', label: AppLabels.Manitoba },
      { value: 'NB', label: AppLabels.NewBrunswick },
      { value: 'NL', label: AppLabels.NewfoundlandAndLabrador },
      { value: 'NS', label: AppLabels.NovaScotia },
      { value: 'ON', label: AppLabels.Ontario },
      { value: 'PE', label: AppLabels.PrinceEdwardIsland },
      { value: 'QC', label: AppLabels.Quebec },
      { value: 'SK', label: AppLabels.Saskatchewan },
      { value: 'NT', label: AppLabels.NorthwestTerritories },
      { value: 'NU', label: AppLabels.Nunavut },
      { value: 'YT', label: AppLabels.Yukon },
    ]), []);

    const yesNoOptions = [
      { value: 1, label: AppLabels.Yes },
      { value: 0, label: AppLabels.No },
    ];

    const availableLocations = useMemo(() => userConfig === undefined || lang === undefined 
        ? [] 
        : provinces.map((x) => ({
            label: x.label,
            value: x.value,
            options: userConfig.locations
                .filter(
                    (y) =>
                    x.value === y.provinceCode &&
                    y.names[lang] !== undefined &&
                    y.names[lang] !== null,
                )
                .sort((y, z) =>
                    y.names[lang].localeCompare(
                    z.names[lang],
                    ),
                )
        })
    ), [ userConfig, lang, provinces ]);

  return {
    goFreddyUrl,
    data,
    setData,
    userConfig,
    setUserConfig,
    isSaving,
    setIsSaving,
    hasSubmitted,
    setHasSubmitted,
    validated,
    setValidated,
    valid,
    setValid,
    serverError,
    setServerError,
    generatedTokensJson,
    setGeneratedTokensJson,
    confirmAddress,
    setConfirmAddress,
    ready,
    setReady,
    pickupProvince,
    setPickupProvince,
    shippingTypeOptions,
    provinces,
    yesNoOptions,
    availableLocations,
    amountNeededOptions,
    setAmountNeededOptions
  }
}

export default useHomeMethods;